import { Grid, IconButton } from '@mui/material'
import { Form, Formik } from 'formik'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { getErrMsg, toastNeg, toastPos } from '../../../common/toastHelper'
import { ILinkedProject, INewSkill, ISkillForm } from '../../../interfaces/skills/ISkills'
import { useStore } from '../../../Provider'
import DeleteConfirmation from '../../OPcomponents/DeleteConfirmation'
import { ExitConfirmation } from '../../OPcomponents/ExitConfirmation'
import AutoSave from '../../OPcomponents/FormikAutosave'
import { MuiButtonVariants, OPButton } from '../../OPcomponents/OPButton'
import { OPCheckBox } from '../../OPcomponents/OPCheckBox'
import { OPMultiline } from '../../OPcomponents/OPMultiline'
import { OPSkillbar } from '../../OPcomponents/OPSkillbar'
import { OPSkillsCombobox } from '../../OPcomponents/OPSkillsCombobox'
import { skillLevelSchema, skillSchema } from '../../OPcomponents/ValidationSchemas'
import '../../styles/grid.scss'
import { DeleteIconBlack } from '../../styles/Icons'
import { OPSkillsButtons } from '../OPSkillsButtons'
import '../profile.scss'

interface userSkill {
  collapseHandler?: () => void
  linkingProject?: boolean
  setLinkingProject?: (value: boolean) => void
  data?: ISkillForm
  isNew?: boolean
  setLinkedProjects?: (val: ILinkedProject[]) => void
  linkedProjects: ILinkedProject[]
  hideProjectLinking?: boolean
}

const UserSkill = (props: userSkill) => {
  const { t, i18n } = useTranslation()
  const { collapseHandler, linkingProject, data, setLinkingProject, linkedProjects, setLinkedProjects, isNew, hideProjectLinking } = props
  const rootStore = useStore()
  const { getAllUserSkills, getPersonInterests } = rootStore.personStore
  const { validatedUser } = rootStore.loginStore
  const {
    getAllSkills,
    clearEditCurrent,
    editUserSkill,
    editCurrentSkill,
    showEditSkillModalHandler,
    showAddProjectToSkillModal,
    setSaving,
    saving,
    deleteNewSkillProject,
    postNewSkillToUser,
    removeLinkedSkillProject,
    getUserDetailedSkillLinkedProjects,
    deleteUserSkill,
    postNewSkillProject,
    allSkills,
  } = rootStore.skillStore
  const { projects, getAllProjects } = rootStore.projectStore

  // Extended form values when creating a brand new skill (mirror AddNewSkill.tsx)
  type INewSkillWithProject = INewSkill & { projectId: number }
  interface SkillOption {
    key: number
    value: string
    text: string
    categoryName?: string
  }

  // Determine the actual skill being viewed (for new user skill, it should be fixed, not selectable)
  const effectiveSkillId = useMemo(() => {
    if (isNew) {
      if (data?.skillId && data.skillId > 0) return data.skillId
      if (editCurrentSkill?.skillId && editCurrentSkill.skillId > 0) return editCurrentSkill.skillId
    }
    return data?.skillId ?? -1
  }, [isNew, data?.skillId, editCurrentSkill?.skillId])

  const initValues: INewSkill | INewSkillWithProject = isNew
    ? {
        personId: validatedUser,
        skillId: effectiveSkillId,
        skillLevel: 0,
        interest: 0,
        comment: '',
        projectId: -1,
        isKeySkill: false,
      }
    : {
        personId: validatedUser,
        skillId: data?.skillId ?? -1,
        skillLevel: data?.skillLevel ?? 0,
        interest: data?.interest ?? 0,
        comment: data?.comment ?? '',
        isKeySkill: data?.isKeySkill ?? false,
      }

  const [deleteSkillConfirmation, setdeleteSkillConfirmation] = useState(false)
  const noop = () => void 0

  const filteredDropdownProjects = useMemo(() => {
    if (!isNew) return []
    return projects
      .filter((proj) => Boolean(proj.userHas))
      .map((project) => ({
        key: project.project.projectId,
        value: String(project.project.projectId),
        text: project.project.name + (project.project.customer ? ` (${project.project.customer})` : ''),
        isEmploymentProject: project.isEmployerJobTitlesProject,
      }))
      .sort((a, b) => {
        if (a.isEmploymentProject !== b.isEmploymentProject) return a.isEmploymentProject ? -1 : 1
        return a.text.localeCompare(b.text, i18n.language, { sensitivity: 'base' })
      })
  }, [projects, isNew, i18n.language])

  // Fetch required data for new skill path (no extra initialization state needed)
  useEffect(() => {
    if (isNew) {
      if (!allSkills || allSkills.length === 0) void getAllSkills(validatedUser)
      if (!projects || projects.length === 0) void getAllProjects(validatedUser)
    }
  }, [isNew, getAllSkills, getAllProjects, validatedUser, allSkills, projects])

  const formSubmit = async (values: INewSkill | INewSkillWithProject) => {
    setSaving(true)
    try {
      if (isNew) {
        await postNewSkillToUser(values, validatedUser, false)
        // Link project if provided
        const v = values as INewSkillWithProject
        if ('projectId' in v && v.projectId > 0) {
          await postNewSkillProject({ userId: validatedUser, projectId: v.projectId, skillId: v.skillId })
        }
        toastPos(t('Toasts.SkillUserAdd'), true)
        closeHandler()
      } else {
        await editUserSkill(values, validatedUser, false)
      }
    } catch (error) {
      toastNeg(getErrMsg(error, t) || t('Toasts.SkillUserAddError'))
    } finally {
      setSaving(false)
    }
  }

  const onProjectRemoveConfirm = async (id: number) => {
    try {
      await deleteNewSkillProject(validatedUser, editCurrentSkill.skillId, id)
      removeLinkedSkillProject(id)
      toastPos(t('Toasts.SkillProjectDelete'), true)
    } catch (error) {
      toastNeg(getErrMsg(error, t) || t('Toasts.SkillProjectDeleteError'), true)
    } finally {
      const linkedProjects = (await getUserDetailedSkillLinkedProjects(validatedUser, editCurrentSkill.skillId)).skill.linkedProjects
      setLinkedProjects?.(linkedProjects)
      await getPersonInterests(validatedUser)
    }
  }

  const removeSkill = async () => {
    setSaving(true)
    try {
      await deleteUserSkill(validatedUser, editCurrentSkill.skillId, false)
      toastPos(t('Toasts.SkillUserDelete'))
    } catch (error) {
      toastNeg(getErrMsg(error, t) || t('Toasts.SkillUserDeleteError'))
    } finally {
      setSaving(false)
      closeHandler()
    }
  }

  const closeHandler = (formClose = true) => {
    if (collapseHandler) collapseHandler()
    setTimeout(async () => {
      clearEditCurrent()
      showEditSkillModalHandler(false)
      if (formClose) {
        await getAllUserSkills(validatedUser, false)
        await getAllSkills(validatedUser, false)
      }
    }, 550)
  }

  return (
    <>
      <Grid container className='gridcontainer--formcontainer__lastitempaddings'>
        <Grid item xs={12} className='griditem--formitem__titlemargin'>
          <span className='myskill--font__titlefont sini'>{t('Skills.SkillForMe')}</span>
        </Grid>
        {(isNew || editCurrentSkill) && (
          <Grid item xs={12}>
            <Formik
              initialValues={initValues}
              onSubmit={formSubmit}
              validationSchema={
                isNew
                  ? skillSchema.concat(
                      Yup.object({
                        projectId: Yup.number().min(1).required(),
                      }),
                    )
                  : skillLevelSchema
              }
              enableReinitialize={Boolean(isNew)}
            >
              {({ isValid, dirty, setFieldValue, values, handleChange, handleSubmit }) => (
                <Form onSubmit={handleSubmit} className='profile--form__formwidth'>
                  <ExitConfirmation when={dirty} />
                  {isNew ? (
                    <Grid container justifyContent='space-between' rowSpacing={2} columnSpacing={4}>
                      <Grid container item xs={12} md={6} spacing={2}>
                        {!hideProjectLinking && (
                          <Grid item xs={12}>
                            <OPSkillsCombobox
                              name='projectId'
                              label={`${t('Projects.EmployerOrProject')} *`}
                              optionList={filteredDropdownProjects}
                              curState={undefined}
                              groupBy={(opt) => (opt?.isEmploymentProject ? t('Projects.Employer') : t('Projects.Project'))}
                              onChange={(_e, val) => {
                                if (val) setFieldValue('projectId', val.key)
                              }}
                              onDelete={() => setFieldValue('projectId', null)}
                            />
                          </Grid>
                        )}
                        <Grid item xs={12}>
                          {(() => {
                            const selected = allSkills.find((s) => s.skill.skillId === effectiveSkillId)?.skill
                            if (!selected) return null
                            const option: SkillOption = {
                              key: selected.skillId,
                              value: String(selected.skillId),
                              text: i18n.language === 'en' && selected.skillNameEng?.length ? selected.skillNameEng : selected.skillNameFi,
                              categoryName: selected.categoryName,
                            }
                            return (
                              <OPSkillsCombobox
                                name='skillId'
                                label={`${t('Discussions.GoalSkill')} *`}
                                optionList={[option]}
                                curState={option}
                                multiple={false}
                                disabled
                                onChange={noop}
                                onDelete={noop}
                                groupBy={(opt) => opt?.categoryName || t('Skills.Category')}
                              />
                            )
                          })()}
                        </Grid>
                        <Grid item xs={12} className='griditem--formitem__formtoppadding'>
                          <OPSkillbar
                            name={'skillLevel'}
                            hasLabel
                            label={`${t('Skills.Level')} *`}
                            value={values.skillLevel ?? 0}
                            onChange={(e, val) => setFieldValue('skillLevel', val)}
                            showLevelText
                          />
                        </Grid>
                        <Grid item xs={12} className='griditem--formitem__formtoppadding'>
                          <OPCheckBox
                            name='isKeySkill'
                            label={t('Skills.KeySkill')}
                            value={values.isKeySkill || false}
                            onChangeState={(_, checked) => setFieldValue('isKeySkill', checked)}
                          />
                        </Grid>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <OPMultiline
                          name='comment'
                          placeholder={`${t('Skills.FurtherInfo')}\u2026`}
                          label={t('Skills.FurtherInfo')}
                          hasCounter
                          maxChars={250}
                          minRows={8}
                          value={values.comment}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <OPSkillsButtons
                          noPadding
                          onClose={() => closeHandler(false)}
                          onSubmit={handleSubmit}
                          bothDisabled={saving}
                          disabled={!(isValid && dirty) || saving}
                        />
                      </Grid>
                    </Grid>
                  ) : (
                    <>
                      <Grid item xs={12}>
                        <OPSkillbar
                          name='skillLevel'
                          hasLabel
                          label={`${t('Skills.Level')}`}
                          value={values.skillLevel ?? undefined}
                          onChange={(e, val) => setFieldValue('skillLevel', val)}
                          disabled={deleteSkillConfirmation}
                          showLevelText
                        />
                      </Grid>
                      <Grid item xs={12} className='griditem--formitem__formtoppadding'>
                        <OPCheckBox
                          name='isKeySkill'
                          label={t('Skills.KeySkill')}
                          value={values.isKeySkill || false}
                          disabled={deleteSkillConfirmation}
                          onChangeState={(_, checked) => setFieldValue('isKeySkill', checked)}
                        />
                      </Grid>
                      <Grid item xs={12} className='griditem--formitem__inneritemmargin'>
                        <OPMultiline
                          width='28.125rem'
                          placeholder={`${t('Skills.FurtherInfo')}\u2026`}
                          label={t('Skills.FurtherInfo')}
                          hasCounter
                          maxChars={1000}
                          minRows={6}
                          value={values.comment}
                          onChange={handleChange}
                          name='comment'
                          disabled={deleteSkillConfirmation}
                        />
                      </Grid>
                      {!hideProjectLinking && (
                        <>
                          <Grid item xs={8} className='griditem--userskill__skilltopmargin'>
                            <span className='myskill--font__titlefont subTitle'>{`${t('Skills.AddedProjects')}`}</span>
                          </Grid>
                          <Grid
                            container
                            className='gridcontainer--addskilltoproject__tablecontainer tabledata'
                            xs={12}
                            lg={6}
                            item
                            rowSpacing={2}
                          >
                            {linkedProjects.length > 0 ? (
                              linkedProjects.map((project) => (
                                <Grid
                                  container
                                  key={project.projectId}
                                  item
                                  className='gridcontainer--addskilltoproject__skillcontainer'
                                  wrap='nowrap'
                                  justifyContent='space-between'
                                >
                                  <Grid item className='gridcontainer--addskilltoproject__hideoverflow'>
                                    <span className='myskill--font__personText'>
                                      {project.projectName + (project.customer ? ` (${project.customer})` : '')}
                                    </span>
                                  </Grid>
                                  <Grid item className='griditem--deletebutton'>
                                    <IconButton
                                      disabled={deleteSkillConfirmation || saving}
                                      className='profile--listitembtn__pointerall'
                                      onClick={() => onProjectRemoveConfirm(project.projectId)}
                                    >
                                      <DeleteIconBlack />
                                    </IconButton>
                                  </Grid>
                                </Grid>
                              ))
                            ) : (
                              <Grid item xs={12}>
                                <span className='myskill--font__regtext'>{`${t('Skills.NoLinkedProjects')}`}</span>
                              </Grid>
                            )}
                          </Grid>
                        </>
                      )}
                      {!hideProjectLinking && (
                        <Grid item xs={12}>
                          <OPButton
                            onClick={() => setLinkingProject?.(true)}
                            label={t('Skills.AddProject')}
                            buttonVariant={MuiButtonVariants.secondary_outlined}
                            disabled={showAddProjectToSkillModal || deleteSkillConfirmation}
                          />
                        </Grid>
                      )}
                      {!linkingProject && (
                        <>
                          <Grid item container xs={12} justifyContent='flex-end'>
                            <Grid item className='griditem--userskill__buttonmargin'>
                              <OPSkillsButtons
                                noPadding
                                isDelete
                                onDelete={() => setdeleteSkillConfirmation(true)}
                                disabled={showAddProjectToSkillModal || saving || !(isValid && dirty)}
                                bothDisabled={saving || showAddProjectToSkillModal || deleteSkillConfirmation}
                                onClose={() => {
                                  closeHandler(false)
                                }}
                                isAddingSkillToSelf
                                onAutosave
                                onSubmit={handleSubmit}
                              />
                            </Grid>
                          </Grid>
                          <Grid item xs={12}>
                            <DeleteConfirmation
                              open={deleteSkillConfirmation}
                              whatToDelete='Skill'
                              deleteHandler={removeSkill}
                              handleClose={() => setdeleteSkillConfirmation(false)}
                            />
                          </Grid>
                        </>
                      )}
                      <AutoSave />
                    </>
                  )}
                </Form>
              )}
            </Formik>
          </Grid>
        )}
      </Grid>
    </>
  )
}

export default observer(UserSkill)
